import { useEffect, useState, useCallback, useMemo, useRef, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import api from '../lib/api'
import { BookOpen, Play, EyeOff, Search, BadgeCheck } from 'lucide-react'
import { useAuthStore, isStaff } from '../stores/authStore'
import { useMyCertificates } from '../hooks/useMyCertificates'
import { getCourseCoverUrl } from '../lib/course-covers'
import { getDifficultyLabel } from '../lib/academy/difficulty'
import { getProgramLabel } from '../config/schools'
import { Button } from '@/components/ui/button'
import { PageHeader, PageEmpty } from '@/components/page'

interface LastLesson {
  id: string
  title: string
  last_position_seconds: number
  last_accessed_at: string
}

interface EnrolledCourse {
  id: string
  title: string
  slug: string
  thumbnail_url: string
  category: string
  difficulty_level: string
  enrollment_status: string
  enrolled_at: string
  total_lessons: number
  completed_lessons: number
  last_lesson?: LastLesson | null
}

// Misma regla que el Dashboard: completar exige también aprobar los exámenes obligatorios,
// así que 100% de lecciones no basta (un curso con examen pendiente sigue 'En curso').
const isCompleted = (c: EnrolledCourse) => c.enrollment_status === 'completed'

function CourseGridSkeleton() {
  return (
    <div role="status" aria-label="Cargando cursos" className="space-y-8">
      <div className="space-y-3">
        <div className="h-9 w-64 animate-pulse rounded-md bg-muted" />
        <div className="h-4 w-80 max-w-full animate-pulse rounded-md bg-muted" />
      </div>
      <div className="grid gap-5 grid-cols-[repeat(auto-fit,minmax(17rem,1fr))] [&>*]:max-w-[40rem]">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-2.5">
            <div className="aspect-video animate-pulse rounded-lg bg-muted" />
            <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
            <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
          </div>
        ))}
      </div>
    </div>
  )
}

function lessonToSlug(title: string) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

// Staff (academy-staff-unrestricted-access): browses the FULL catalog
// (published and unpublished) via the already staff-gated GET
// /courses/admin/all, instead of only their own enrollments — opening any
// course's lessons then works via the staff bypass in lessons.ts, no
// Enrollment row involved.
interface StaffCourse {
  id: string
  title: string
  slug: string
  thumbnailUrl: string | null
  category: string | null
  difficultyLevel: string
  isPublished: boolean
}

function StaffCourseCatalog() {
  const [courses, setCourses] = useState<StaffCourse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const loadCourses = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await api.get('/academy/courses/admin/all', { params: { limit: 100 } })
      setCourses(res.data.data?.courses ?? [])
    } catch (err) {
      console.error(err)
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadCourses() }, [loadCourses])

  if (loading) {
    return <CourseGridSkeleton />
  }

  if (error) {
    return (
      <PageEmpty
        title="No pudimos cargar el listado de cursos"
        description="Revisa tu conexión e intenta de nuevo."
        action={<Button onClick={loadCourses}>Reintentar</Button>}
      />
    )
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="Todos los cursos"
        description="Acceso de staff sin restricciones — incluye cursos no publicados."
      />

      {courses.length === 0 ? (
        <PageEmpty icon={<BookOpen className="h-8 w-8" />} title="Aún no hay cursos" />
      ) : (
        <div className="grid gap-5 grid-cols-[repeat(auto-fit,minmax(17rem,1fr))] [&>*]:max-w-[40rem]">
          {courses.map((course) => (
            <Link key={course.id} to={`/courses/${course.slug}`} className="group flex flex-col gap-2.5 rounded-lg focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50">
              <div className="relative aspect-video overflow-hidden rounded-lg bg-muted">
                {course.thumbnailUrl ? (
                  <img
                    src={course.thumbnailUrl}
                    alt={course.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-secondary text-secondary-foreground/60" aria-hidden="true">
                    <BookOpen className="h-8 w-8" />
                  </div>
                )}
                {!course.isPublished && (
                  <div className="absolute left-2 top-2 flex items-center gap-1 rounded bg-black/80 px-1.5 py-0.5 text-micro font-bold text-white">
                    <EyeOff className="h-3 w-3" />
                    No publicado
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Play className="h-4 w-4 fill-current" />
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-micro text-muted-foreground">
                  <span>{course.category ? getProgramLabel(course.category) : "Sin categoría"}</span>
                  <span>·</span>
                  <span>{getDifficultyLabel(course.difficultyLevel) ?? course.difficultyLevel}</span>
                </div>
                <h3 className="mt-1 line-clamp-2 text-sm font-bold leading-snug text-foreground transition-colors group-hover:text-primary">
                  {course.title}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export default function MyCourses() {
  const user = useAuthStore((s) => s.user)
  const [courses, setCourses] = useState<EnrolledCourse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<'all' | 'progress' | 'done'>('all')
  // Entrada escalonada solo en el primer render con datos; los cambios de filtro usan un fundido corto.
  // `staggered` pasa a true en un efecto tras ese primer render, nunca durante el render.
  const [staggered, setStaggered] = useState(false)
  const introClass = useRef(new Map<string, string>())
  useEffect(() => {
    if (!loading) setStaggered(true)
  }, [loading])
  const { find: findCertificate } = useMyCertificates(!isStaff(user))

  const loadCourses = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await api.get('/academy/courses/enrolled/me')
      setCourses(res.data.data || [])
    } catch (err) {
      console.error(err)
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isStaff(user)) loadCourses()
  }, [loadCourses, user])

  const visibleCourses = useMemo(() => {
    const q = query.trim().toLowerCase()
    return courses.filter((c) => {
      const done = isCompleted(c)
      if (filter === 'done' && !done) return false
      if (filter === 'progress' && done) return false
      return !q || c.title.toLowerCase().includes(q) || (c.category ?? '').toLowerCase().includes(q)
    })
  }, [courses, query, filter])

  // Tras cada commit se fija la clase de entrada de cada curso ya pintado, para que un
  // re-render posterior no cambie la animación (y no la reinicie).
  useEffect(() => {
    if (loading) return
    for (const c of visibleCourses) {
      if (!introClass.current.has(c.id)) introClass.current.set(c.id, staggered ? 'motion-fade' : 'motion-stagger-item')
    }
  }, [loading, visibleCourses, staggered])

  if (isStaff(user)) {
    return <StaffCourseCatalog />
  }

  if (loading) {
    return <CourseGridSkeleton />
  }

  if (error) {
    return (
      <PageEmpty
        title="No pudimos cargar tus cursos"
        description="Hubo un problema de conexión. Intenta de nuevo en unos segundos."
        action={<Button onClick={loadCourses}>Reintentar</Button>}
      />
    )
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="Mis cursos"
        description="Todos los cursos en los que estás inscrito. Entra directo a estudiar."
      />

      {courses.length === 0 ? (
        <PageEmpty
          icon={<BookOpen className="h-8 w-8" />}
          title="Aún no tienes cursos"
          description="Mira los cursos disponibles para ti y empieza tu primera lección."
          action={
            <Link to="/dashboard" className="inline-flex min-h-11 items-center rounded-lg bg-primary px-5 text-sm font-bold text-primary-foreground hover:brightness-110 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50">
              Ver cursos disponibles
            </Link>
          }
        />
      ) : (
        <>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar en mis cursos"
              aria-label="Buscar en mis cursos"
              className="h-11 w-full rounded-lg border border-input bg-transparent pl-9 pr-3 text-sm outline-none focus-visible:ring-3 focus-visible:ring-ring/50 sm:h-9"
            />
          </div>
          <div role="group" aria-label="Filtrar por estado" className="flex gap-1.5">
            {([['all', 'Todos'], ['progress', 'En curso'], ['done', 'Completados']] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
                className={`motion-press h-11 rounded-lg border px-3 text-sm font-medium transition-[background-color,border-color,color,transform] duration-(--dur-fast) focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 sm:h-9 ${filter === key ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:bg-accent'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {visibleCourses.length === 0 && (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Ningún curso coincide con tu búsqueda.{' '}
            <button type="button" className="font-medium text-primary underline underline-offset-4" onClick={() => { setQuery(''); setFilter('all') }}>Limpiar filtros</button>
          </p>
        )}
        <div className="grid gap-5 grid-cols-[repeat(auto-fit,minmax(17rem,1fr))] [&>*]:max-w-[40rem]">
          {visibleCourses.map((course, i) => {
            // Se lee la clase ya fijada; el mapa solo se completa tras el primer render con datos.
            const intro = introClass.current.get(course.id) ?? (staggered ? 'motion-fade' : 'motion-stagger-item')
            const prog = course.total_lessons > 0 ? Math.round((course.completed_lessons / course.total_lessons) * 100) : 0
            const studyUrl = course.last_lesson
              ? `/courses/${course.slug}/${lessonToSlug(course.last_lesson.title)}`
              : `/courses/${course.slug}`
            const done = isCompleted(course)
            const cert = findCertificate({ id: course.id, slug: course.slug, title: course.title })
            const certCode = cert.status === 'found' ? cert.code : null
            return (
              <div key={course.id} className={`${intro} flex flex-col gap-2.5`} style={{ '--i': Math.min(i, 7) } as CSSProperties}>
              <Link to={studyUrl} className="group flex flex-col gap-2.5 rounded-lg focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50">
                <div className="relative aspect-video overflow-hidden rounded-lg bg-muted">
                  <img
                    src={getCourseCoverUrl(course)}
                    alt={course.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute bottom-2 right-2 rounded bg-black/80 px-1.5 py-0.5 text-micro font-bold text-white">
                    {course.completed_lessons}/{course.total_lessons}
                  </div>
                  <div className="absolute bottom-0 left-0 h-1 w-full bg-black/20">
                    <div
                      role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={prog}
                      aria-label={`Progreso del curso ${course.title}`}
                      className="motion-progress motion-progress-in h-full w-full bg-primary"
                      style={{ transform: `scaleX(${prog / 100})` }}
                    />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <Play className="h-4 w-4 fill-current" />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-1.5 text-micro text-muted-foreground">
                    <span className="font-semibold tabular-nums text-foreground">{prog}%</span>
                    <span>·</span>
                    <span>{getProgramLabel(course.category)}</span>
                    <span>·</span>
                    <span>{getDifficultyLabel(course.difficulty_level) ?? course.difficulty_level}</span>
                  </div>
                  <h3 className="mt-1 line-clamp-2 text-sm font-bold leading-snug text-foreground transition-colors group-hover:text-primary">
                    {course.title}
                  </h3>
                </div>
              </Link>
              {done && (certCode ? (
                <Link to={`/certificates/${certCode}`} className="inline-flex min-h-11 w-fit items-center gap-1.5 rounded-lg border border-border px-3 text-xs font-semibold text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 sm:min-h-8">
                  <BadgeCheck className="h-4 w-4 text-primary" aria-hidden="true" />
                  Ver certificado
                </Link>
              ) : (
                <span className="inline-flex w-fit items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                  <BadgeCheck className="h-4 w-4" aria-hidden="true" />
                  Completado
                </span>
              ))}
              </div>
            )
          })}
        </div>
        </>
      )}
    </div>
  )
}
